import React, { FC, useEffect, useState } from "react";
import { formatDateTime } from "../../../core/format";
import DeviceTableRow from "../../../models/DeviceTableRow";
import styles from "./DeviceDetailsDialog.module.scss";
import { TurnKeyService } from "../../../services/turnkey.service";
import { TURNKEY_VENDOR_NAME } from "../../../models/TurnKeyApi";
import SpinnerOverlay from "../../../elements/Spinner/SpinnerOverlay";

interface DeviceDetailsDialogProps {
  dataRow: DeviceTableRow;
}

const LOCK_NUMBER_KEY = "lock_number";
const PRIVATE_MASTER_CODE_KEY = "private_master_code";

type VendorData = Record<string, unknown>;
type VendorDataEntry = [string, unknown];

interface ModalState {
  vendorData: VendorData;
  privateMasterCode?: unknown;
  isLoadingPrivateMasterCode: boolean;
}

const hasValue = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== "";

const needsPrivateMasterCode = (
  dataRow: DeviceTableRow,
  vendorData: VendorData,
  privateMasterCode: unknown = vendorData[PRIVATE_MASTER_CODE_KEY],
): boolean =>
  dataRow.vendor === TURNKEY_VENDOR_NAME &&
  hasValue(vendorData[LOCK_NUMBER_KEY]) &&
  !hasValue(privateMasterCode);

const privateMasterCodeCache = new Map<string, string>();
const pendingPrivateMasterCodeRequests = new Map<
  string,
  Promise<string | null | undefined>
>();

const getModalState = (dataRow: DeviceTableRow): ModalState => {
  const vendorData: VendorData = { ...(dataRow.vendorData ?? {}) };
  const lockNumber = vendorData[LOCK_NUMBER_KEY];
  const cachedPrivateMasterCode = hasValue(lockNumber)
    ? privateMasterCodeCache.get(String(lockNumber))
    : undefined;
  const privateMasterCode = hasValue(vendorData[PRIVATE_MASTER_CODE_KEY])
    ? vendorData[PRIVATE_MASTER_CODE_KEY]
    : cachedPrivateMasterCode;

  return {
    vendorData,
    privateMasterCode,
    isLoadingPrivateMasterCode: needsPrivateMasterCode(
      dataRow,
      vendorData,
      privateMasterCode,
    ),
  };
};

// add PMC after lock_number field
const getVendorDataEntries = (
  vendorData: VendorData,
  privateMasterCode: unknown,
): VendorDataEntry[] => {
  const entries = Object.entries(vendorData).flatMap<VendorDataEntry>(
    ([key, value]) => {
      if (key === PRIVATE_MASTER_CODE_KEY) return [];

      const rowEntries: VendorDataEntry[] = [[key, value]];
      if (key === LOCK_NUMBER_KEY && hasValue(privateMasterCode)) {
        rowEntries.push([PRIVATE_MASTER_CODE_KEY, privateMasterCode]);
      }

      return rowEntries;
    },
  );

  const hasLockNumber = entries.some(([key]) => key === LOCK_NUMBER_KEY);
  if (hasValue(privateMasterCode) && !hasLockNumber) {
    entries.push([PRIVATE_MASTER_CODE_KEY, privateMasterCode]);
  }

  return entries;
};

const getPrivateMasterCode = (lockNumber: string) => {
  const cachedPrivateMasterCode = privateMasterCodeCache.get(lockNumber);
  if (cachedPrivateMasterCode) return Promise.resolve(cachedPrivateMasterCode);

  const pendingRequest = pendingPrivateMasterCodeRequests.get(lockNumber);
  if (pendingRequest) return pendingRequest;

  const service = new TurnKeyService();
  const request = service
    .getLockInformation(lockNumber)
    .then((turnkeyData) => {
      const privateMasterCode = turnkeyData?.[PRIVATE_MASTER_CODE_KEY];
      if (privateMasterCode) {
        privateMasterCodeCache.set(lockNumber, privateMasterCode);
      }

      return privateMasterCode;
    })
    .finally(() => {
      pendingPrivateMasterCodeRequests.delete(lockNumber);
    });

  pendingPrivateMasterCodeRequests.set(lockNumber, request);
  return request;
};

const DeviceDetailsDialog: FC<DeviceDetailsDialogProps> = ({ dataRow }) => {
  const emptyValue = "N/A";
  const [modalState, setModalState] = useState<ModalState>(() =>
    getModalState(dataRow),
  );
  const { vendorData, privateMasterCode, isLoadingPrivateMasterCode } =
    modalState;
  const vendorDataEntries = getVendorDataEntries(vendorData, privateMasterCode);

  const formatKey = (key: string): string => {
    if (key === PRIVATE_MASTER_CODE_KEY) return "Private Master Code (PMC)";
    return key.replace(/_|-/g, " ");
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined) return "null";
    else if (typeof value === "object") return JSON.stringify(value);
    return value;
  };

  useEffect(() => {
    const initialModalState = getModalState(dataRow);
    const lockNumber = initialModalState.vendorData[LOCK_NUMBER_KEY];

    setModalState(initialModalState);

    if (!initialModalState.isLoadingPrivateMasterCode) return;

    let isTurnKeyRequestActive = true;

    const loadPrivateMasterCode = async () => {
      try {
        const privateMasterCode = await getPrivateMasterCode(
          String(lockNumber),
        );
        if (!privateMasterCode) return;

        if (!isTurnKeyRequestActive) return;

        setModalState({
          ...initialModalState,
          privateMasterCode,
          isLoadingPrivateMasterCode: false,
        });
      } catch (error) {
        console.error("Unable to load TurnKey lock information", error);
      } finally {
        if (isTurnKeyRequestActive) {
          setModalState((currentState) => ({
            ...currentState,
            isLoadingPrivateMasterCode: false,
          }));
        }
      }
    };

    loadPrivateMasterCode();

    return () => {
      isTurnKeyRequestActive = false;
    };
  }, [dataRow]);

  return (
    <div
      className={styles.DeviceDetailsDialog}
      data-testid="DeviceDetailsDialog"
    >
      <div
        className={`details-dialog ${
          isLoadingPrivateMasterCode ? styles.LoadingContent : ""
        }`}
      >
        <b className="details-dialog-title">Device Details</b>
        {vendorDataEntries.map(([key, value]) => {
          return (
            <div key={key}>
              <div className="row">
                <label className="col detail-key">{formatKey(key)}:</label>
                <label className="col detail-value">{formatValue(value)}</label>
              </div>
            </div>
          );
        })}
        <div>
          <div className="row">
            <label className="col detail-key">Installed At:</label>
            <label className="col detail-value">
              {dataRow.createdAt
                ? formatDateTime(dataRow.createdAt)
                : emptyValue}
            </label>
          </div>
        </div>
        <div>
          <div className="row">
            <label className="col detail-key">Updated At:</label>
            <label className="col detail-value">
              {dataRow.updatedAt
                ? formatDateTime(dataRow.updatedAt)
                : emptyValue}
            </label>
          </div>
        </div>
      </div>
      {isLoadingPrivateMasterCode && <SpinnerOverlay />}
    </div>
  );
};

export default DeviceDetailsDialog;
